import { endOfDay, startOfDay } from "date-fns";

/**
 * Normalizes a Mongo/Object id from table rows, populated refs, or strings.
 * @param {unknown} value
 * @returns {string}
 */
export const normalizeId = (value) => {
  if (value == null || value === "") {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    return String(value._id || value.id || "");
  }

  return String(value);
};

/**
 * Driver/resource id for an assigned table row (trucking leg or service job).
 * @param {Record<string, unknown>} leg
 * @returns {string}
 */
export const getAssignedRowDriverId = (leg) => {
  if (!leg) {
    return "";
  }

  const driver = leg.driver ?? leg.assignedTo?.[0] ?? leg.assignedTo;
  return normalizeId(driver);
};

/**
 * Best date to scroll the timeline for a leg/job row.
 * @param {Record<string, unknown>} leg
 * @returns {Date|null}
 */
export const getAssignedLegAnchorDate = (leg) => {
  if (!leg) {
    return null;
  }

  const candidates = [
    leg.appointmentDate,
    leg.startDate,
    leg.transactionDate,
    leg.timeRange?.start,
    leg.dateCreated,
    leg.createdAt,
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

/**
 * Expands the scheduler date range so an event anchor day is visible.
 * @param {{ start: Date, end: Date }} currentRange
 * @param {Date} eventStart
 * @returns {{ start: Date, end: Date, changed: boolean }}
 */
export const buildDateRangeIncludingEvent = (currentRange, eventStart) => {
  const anchor = new Date(eventStart);
  const currentStart = startOfDay(currentRange?.start || anchor);
  const currentEnd = endOfDay(currentRange?.end || anchor);
  const anchorStart = startOfDay(anchor);
  const anchorEnd = endOfDay(anchor);

  let nextStart = currentStart;
  let nextEnd = currentEnd;
  let changed = false;

  if (anchorStart < nextStart) {
    nextStart = anchorStart;
    changed = true;
  }

  if (anchorEnd > nextEnd) {
    nextEnd = anchorEnd;
    changed = true;
  }

  return {
    start: nextStart,
    end: nextEnd,
    changed,
  };
};

/**
 * @param {Record<string, unknown>} event
 * @returns {string}
 */
const getSchedulerEventId = (event) => {
  return normalizeId(event?.id || event?._id || event?.legId);
};

/**
 * @param {Record<string, unknown>} leg
 * @returns {string}
 */
const getAssignedRowId = (leg) => {
  return normalizeId(leg?._id || leg?.id || leg?.legId);
};

/**
 * Finds the scheduler event that corresponds to an assigned table row.
 * @param {Array<Record<string, unknown>>} scheduledEvents
 * @param {Record<string, unknown>} leg
 * @returns {Record<string, unknown>|null}
 */
export const findSchedulerEventForAssignedRow = (scheduledEvents, leg) => {
  if (!leg || !Array.isArray(scheduledEvents) || scheduledEvents.length === 0) {
    return null;
  }

  const legId = getAssignedRowId(leg);
  if (!legId) {
    return null;
  }

  const directMatch = scheduledEvents.find(
    (event) => getSchedulerEventId(event) === legId,
  );
  if (directMatch) {
    return directMatch;
  }

  const legIdMatch = scheduledEvents.find(
    (event) => normalizeId(event?.legId) === legId,
  );
  if (legIdMatch) {
    return legIdMatch;
  }

  const jobId = normalizeId(leg?.jobId);
  if (!jobId) {
    return null;
  }

  const driverId = getAssignedRowDriverId(leg);

  return (
    scheduledEvents.find((event) => {
      const eventJobId = normalizeId(event?.jobId);
      if (eventJobId !== jobId) {
        return false;
      }

      if (!driverId) {
        return true;
      }

      const assigned = Array.isArray(event?.assignedResources)
        ? event.assignedResources
        : [];

      return assigned.some((resource) => normalizeId(resource) === driverId);
    }) || null
  );
};

/**
 * Builds scroll/highlight payload when the event is not yet loaded in the current range.
 * @param {Record<string, unknown>} leg
 * @returns {{ eventId: string, eventStart: Date, resourceId: string }|null}
 */
export const buildScrollTargetFromAssignedRow = (leg) => {
  const anchorDate = getAssignedLegAnchorDate(leg);
  const resourceId = getAssignedRowDriverId(leg);
  const eventId = getAssignedRowId(leg);

  if (!anchorDate || !resourceId || !eventId) {
    return null;
  }

  return {
    eventId,
    eventStart: anchorDate,
    resourceId,
  };
};
